import * as React from "react"

import {
  Button,
  Label,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Switch,
  cn,
} from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { MobileFormSheet } from "@/shared/components/mobile-form"
import { MobileSegmentedControl } from "@/shared/components/mobile-list-chrome"

/** A phone settings row: label left, control right, the kit's row height (inside the sheet's grouped card). */
const MOBILE_ROW = "flex min-h-[3.25rem] items-center justify-between gap-4 px-4"

/**
 * Session-local widget configuration with draft/commit semantics: the drawer edits a `draft`, and the
 * widget only re-reads `config` on Save; Cancel (or closing the drawer) restores the draft to `config`.
 * (The dashboard engine has no persistence, so config lives for the session — matches the engine's model.)
 */
export function useWidgetConfig<T extends object>(defaults: T) {
  const [config, setConfig] = React.useState<T>(defaults)
  const [draft, setDraft] = React.useState<T>(defaults)
  const [open, setOpen] = React.useState(false)

  const openDrawer = React.useCallback(() => {
    setDraft(config)
    setOpen(true)
  }, [config])
  const save = React.useCallback(() => {
    setConfig(draft)
    setOpen(false)
  }, [draft])
  const cancel = React.useCallback(() => {
    setDraft(config)
    setOpen(false)
  }, [config])
  const patch = React.useCallback((next: Partial<T>) => setDraft((prev) => ({ ...prev, ...next })), [])

  return { config, draft, patch, open, setOpen, openDrawer, save, cancel }
}

export interface WidgetConfigDrawerProps {
  open: boolean
  title: string
  onSave: () => void
  onCancel: () => void
  children: React.ReactNode
}

/** Right-side drawer shell for a widget's configuration (Save / Cancel; closing = Cancel). */
export function WidgetConfigDrawer({ open, title, onSave, onCancel, children }: WidgetConfigDrawerProps) {
  const t = useTranslations("finance.overview")
  const isMobile = useIsMobile()
  if (isMobile) {
    // Phone: the app's bottom sheet, the settings as one grouped card of rows, Annuler / Enregistrer pills.
    return (
      <MobileFormSheet
        open={open}
        onOpenChange={(next) => {
          if (!next) onCancel()
        }}
        title={title}
        note={t("widgets.common.configureHint")}
        cancelLabel={t("widgets.common.cancel")}
        saveLabel={t("widgets.common.save")}
        onSave={onSave}
      >
        <div className="divide-y divide-border/60 overflow-hidden rounded-2xl bg-card">{children}</div>
      </MobileFormSheet>
    )
  }
  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel()
      }}
      mobileBottomSheet
    >
      <SheetContent side="right" className="flex flex-col gap-0">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>{t("widgets.common.configureHint")}</SheetDescription>
        </SheetHeader>
        <div className="flex-1 space-y-1 overflow-y-auto px-4 py-2">{children}</div>
        <SheetFooter className="flex-row justify-end gap-2">
          <Button variant="outline" onClick={onCancel}>
            {t("widgets.common.cancel")}
          </Button>
          <Button onClick={onSave}>{t("widgets.common.save")}</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

/** A labelled on/off row for the config drawer. */
export function ConfigSwitchRow({
  label,
  checked,
  onCheckedChange,
}: {
  label: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  const id = React.useId()
  const isMobile = useIsMobile()
  return (
    <div className={isMobile ? MOBILE_ROW : "flex items-center justify-between gap-4 py-2"}>
      <Label htmlFor={id} className={isMobile ? "min-w-0 text-base font-semibold" : "text-sm font-normal"}>
        {label}
      </Label>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  )
}

/** A labelled stepper (min…max) for row counts etc. */
export function ConfigStepperRow({
  label,
  value,
  onChange,
  min = 1,
  max = 10,
}: {
  label: string
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
}) {
  const clamp = (n: number) => Math.max(min, Math.min(max, n))
  const isMobile = useIsMobile()
  return (
    <div className={isMobile ? MOBILE_ROW : "flex items-center justify-between gap-4 py-2"}>
      <span className={isMobile ? "min-w-0 text-base font-semibold" : "text-sm"}>{label}</span>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          className={isMobile ? "size-9 rounded-full" : undefined}
          disabled={value <= min}
          onClick={() => onChange(clamp(value - 1))}
          aria-label="Decrease"
        >
          −
        </Button>
        <span className="w-6 text-center text-sm font-medium tabular-nums">{value}</span>
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          className={isMobile ? "size-9 rounded-full" : undefined}
          disabled={value >= max}
          onClick={() => onChange(clamp(value + 1))}
          aria-label="Increase"
        >
          +
        </Button>
      </div>
    </div>
  )
}

/**
 * A checklist for choosing WHICH items (accounts, funds…) a widget shows. `selectedIds === null` means
 * "all" (the default, so every row starts checked); toggling one off switches to an explicit selection.
 */
export function ConfigCheckList({
  label,
  emptyLabel,
  items,
  selectedIds,
  onChange,
}: {
  label: string
  emptyLabel: string
  items: Array<{ id: string; label: string }>
  selectedIds: string[] | null
  onChange: (ids: string[]) => void
}) {
  const isChecked = (id: string) => selectedIds === null || selectedIds.includes(id)
  const toggle = (id: string, checked: boolean) => {
    const base = selectedIds ?? items.map((item) => item.id)
    onChange(checked ? Array.from(new Set([...base, id])) : base.filter((existing) => existing !== id))
  }
  const isMobile = useIsMobile()
  if (isMobile) {
    // Phone: a quiet header row, then one row per item, all inside the sheet's grouped card.
    return (
      <>
        <div className="px-4 pt-3 pb-2 text-sm font-bold text-muted-foreground">{label}</div>
        {items.length === 0 ? (
          <p className="px-4 py-3 text-sm font-semibold text-muted-foreground">{emptyLabel}</p>
        ) : (
          items.map((item) => (
            <label key={item.id} className={MOBILE_ROW}>
              <span className="min-w-0 flex-1 truncate text-base font-semibold">{item.label}</span>
              <Switch checked={isChecked(item.id)} onCheckedChange={(checked) => toggle(item.id, checked)} />
            </label>
          ))
        )}
      </>
    )
  }
  return (
    <div className="space-y-1.5 py-2">
      <span className="text-sm">{label}</span>
      <div className="max-h-72 space-y-0.5 overflow-y-auto rounded-lg border p-1">
        {items.length === 0 ? (
          <p className="px-2 py-2 text-xs text-muted-foreground">{emptyLabel}</p>
        ) : (
          items.map((item) => (
            <label
              key={item.id}
              className="flex cursor-pointer items-center justify-between gap-3 rounded-md px-2 py-1.5 hover:bg-muted/60"
            >
              <span className="min-w-0 flex-1 truncate text-sm">{item.label}</span>
              <Switch checked={isChecked(item.id)} onCheckedChange={(checked) => toggle(item.id, checked)} />
            </label>
          ))
        )}
      </div>
    </div>
  )
}

/** A labelled segmented choice for the config drawer. */
export function ConfigChoiceRow<T extends string>({
  label,
  value,
  options,
  onChange,
  className,
}: {
  label: string
  value: T
  options: Array<{ value: T; label: string }>
  onChange: (value: T) => void
  className?: string
}) {
  const isMobile = useIsMobile()
  if (isMobile) {
    return (
      <div className="flex flex-col gap-2 px-4 py-3">
        <span className="text-base font-semibold">{label}</span>
        <MobileSegmentedControl value={value} items={options} onChange={onChange} />
      </div>
    )
  }
  return (
    <div className={cn("space-y-1.5 py-2", className)}>
      <span className="text-sm">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((option) => (
          <Button
            key={option.value}
            type="button"
            size="sm"
            variant={option.value === value ? "default" : "outline"}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </Button>
        ))}
      </div>
    </div>
  )
}
